<template>
  <b-modal
      id="recover-orphans-modal"
      ref="modal"
      size="xl"
      centered
      scrollable
      :title="'Récupération des orphelins'"
      @show="loadPreview"
      @hidden="reset"
  >
    <div v-if="loading" class="text-center py-10">
      <div class="spinner-border text-primary"></div>
      <div class="mt-3">Recherche des contrats source...</div>
    </div>
    <div v-else>
      <div v-if="!sources.length" class="alert alert-info">
        Aucun contrat source ne redirige vers ce contrat. Rien à récupérer.
      </div>
      <div v-else>
        <div class="alert alert-warning py-2 small">
          <i class="fad fa-info-circle me-1"></i>
          Cochez les contrats source dont les avenants et items doivent être migrés vers ce contrat.
        </div>
        <div class="d-flex mb-3">
          <button class="btn btn-light-primary btn-sm me-2" @click="selectAll">Tout cocher</button>
          <button class="btn btn-light btn-sm" @click="clearAll">Tout décocher</button>
          <div class="ms-auto text-muted small">
            {{ selected.length }} / {{ sources.length }} sélectionné(s) —
            {{ totalAvenants }} avenant(s), {{ totalItems }} item(s)
          </div>
        </div>
        <div class="table-responsive">
          <table class="table table-hover align-middle">
            <thead class="bg-light">
            <tr>
              <th style="width:60px">
                <input type="checkbox" :checked="allSelected" @change="toggleAll"/>
              </th>
              <th>Police</th>
              <th>Créé le</th>
              <th>Supprimé le</th>
              <th class="text-center">Avenants</th>
              <th class="text-center">Items</th>
              <th>UUID</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="s in sources" :key="s.source_uuid"
                :class="{'table-primary': isSelected(s.source_uuid)}">
              <td>
                <input type="checkbox"
                       :value="s.source_uuid"
                       v-model="selected"/>
              </td>
              <td><code>{{ s.police || '—' }}</code></td>
              <td>
                <span v-b-tooltip.hover :title="s.created_at">{{ formatDate(s.created_at) }}</span>
              </td>
              <td>
                <span v-if="s.deleted_at" class="badge badge-light-dark" v-b-tooltip.hover :title="s.deleted_at">
                  {{ formatDate(s.deleted_at) }}
                </span>
                <span v-else class="badge badge-light-success">Actif</span>
              </td>
              <td class="text-center">{{ s.avenants }}</td>
              <td class="text-center">{{ s.items }}</td>
              <td class="text-muted small"><code>{{ s.source_uuid }}</code></td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <template #modal-footer="{ cancel }">
      <button class="btn btn-light" @click="cancel">Annuler</button>
      <button class="btn btn-info"
              :disabled="!canRecover || recovering"
              @click="confirmRecover">
        <span v-if="recovering" class="spinner-border spinner-border-sm me-2"></span>
        <i v-else class="fad fa-heart-pulse me-1"></i>
        Récupérer {{ selected.length }} source(s)
      </button>
    </template>
  </b-modal>
</template>

<script lang="ts">
import {Component, Prop, Vue} from "vue-property-decorator";
import {api} from "@/services/Api";
import Swal from "sweetalert2";

@Component
export default class RecoverOrphansModal extends Vue {
  @Prop({required: true}) contractUuid!: string;

  loading = false;
  recovering = false;
  sources: any[] = [];
  selected: string[] = [];

  get canRecover(): boolean {
    return this.selected.length > 0;
  }

  get allSelected(): boolean {
    return this.sources.length > 0 && this.selected.length === this.sources.length;
  }

  get totalAvenants(): number {
    return this.sources
        .filter(s => this.isSelected(s.source_uuid))
        .reduce((acc, s) => acc + Number(s.avenants || 0), 0);
  }

  get totalItems(): number {
    return this.sources
        .filter(s => this.isSelected(s.source_uuid))
        .reduce((acc, s) => acc + Number(s.items || 0), 0);
  }

  isSelected(uuid: string): boolean {
    return this.selected.indexOf(uuid) !== -1;
  }

  selectAll() {
    this.selected = this.sources.map(s => s.source_uuid);
  }

  clearAll() {
    this.selected = [];
  }

  toggleAll() {
    if (this.allSelected) this.clearAll(); else this.selectAll();
  }

  formatDate(iso: string | null): string {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR");
  }

  reset() {
    this.sources = [];
    this.selected = [];
  }

  async loadPreview() {
    this.reset();
    this.loading = true;
    try {
      const res = await api.get(api.core, "contract/orphans/preview/" + this.contractUuid);
      if (res && res.data && res.data.status === "success") {
        this.sources = res.data.sources || [];
        this.selected = this.sources.map(s => s.source_uuid);
      }
    } catch (e) {
      console.error(e);
    } finally {
      this.loading = false;
    }
  }

  async confirmRecover() {
    if (!this.canRecover) return;
    const list = this.sources
        .filter(s => this.isSelected(s.source_uuid))
        .map(s => `<li><code>${s.police || s.source_uuid}</code> — ${s.avenants} avenant(s), ${s.items} item(s)</li>`)
        .join("");
    const result = await Swal.fire({
      title: "Confirmer la récupération ?",
      html: `Migrer vers ce contrat :<ul class='text-start'>${list}</ul>Total : <b>${this.totalAvenants}</b> avenant(s), <b>${this.totalItems}</b> item(s).`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Récupérer",
      cancelButtonText: "Annuler",
    });
    if (!result.isConfirmed) return;

    this.recovering = true;
    try {
      const res = await api.post(api.core, "contract/recover/orphans/" + this.contractUuid, {
        sources: this.selected,
      });
      if (res && res.data && res.data.status === "success") {
        await Swal.fire({
          icon: "success",
          title: "Récupération terminée",
          html: `${res.data.avenants_transferred ?? 0} avenant(s) et ${res.data.items_transferred ?? 0} item(s) transféré(s).`,
        });
        (this.$refs.modal as any).hide();
        this.$emit("recovered", res.data);
      } else {
        await Swal.fire({title: "Erreur", text: res?.data?.message || "Échec", icon: "error"});
      }
    } catch (e: any) {
      await Swal.fire({title: "Erreur", text: e?.response?.data?.message || e?.message || "Échec", icon: "error"});
    } finally {
      this.recovering = false;
    }
  }
}
</script>
